import { useState, useEffect } from 'react'
import Card from './ui/Card'
import Icon from './ui/Icon'
import NumberDisplay from './WakeUp/NumberDisplay'
import StatusSelector from './WakeUp/StatusSelector'
import NextNumberButton from './WakeUp/NextNumberButton'
import EmptyNumberCard from './WakeUp/EmptyNumberCard'
import CallingHistory from './WakeUp/CallingHistory'

/**
 * WakeUpNumberCard Component
 * Main component for displaying wake up number information
 * Simplified and modularized for better maintainability
 */
const WakeUpNumberCard = ({ 
  number, 
  trackingId,
  remainingCalls,
  maxCalls,
  onStatusUpdate, 
  onRequestNext,
  canRequestNext,
  loading,
  showUpdateButton = true,
  rateLimited = false,
  errorMessage = null,
  errorReason = null,
  region = null,
  availableCount = 0,
  rateLimitWaitTime = 0
}) => {
  const [showNextButton, setShowNextButton] = useState(rateLimited)
  const [cooldownMessage, setCooldownMessage] = useState('')
  const [statusSelected, setStatusSelected] = useState(false)
  const [remainingWaitTime, setRemainingWaitTime] = useState(rateLimitWaitTime)
  const [hasClickedCall, setHasClickedCall] = useState(false)

  // Reset component state when a new number is loaded
  useEffect(() => {
    setShowNextButton(rateLimited)
    setCooldownMessage('')
    setStatusSelected(false)
    setRemainingWaitTime(rateLimitWaitTime)
    setHasClickedCall(false)
  }, [number, trackingId, rateLimited, rateLimitWaitTime])

  // Countdown timer for rate limit
  useEffect(() => {
    if (remainingWaitTime <= 0 || !rateLimited) return

    const timer = setInterval(() => {
      setRemainingWaitTime(prev => {
        const newTime = prev - 1
        if (newTime <= 0) {
          return 0
        }
        return newTime
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [remainingWaitTime, rateLimited])

  // Handle call initiation
  const makeCall = (phoneNumber) => {
    setHasClickedCall(true)
    window.open(`tel:${phoneNumber}`, '_self')
  }

  // Handle status update completion
  const handleStatusUpdate = async (trackingId, statusId, phoneNumber) => {
    await onStatusUpdate(trackingId, statusId, phoneNumber)
    setShowNextButton(true)
    setStatusSelected(true)
  }

  // Handle next number request
  const handleRequestNext = async () => {
    setCooldownMessage('')
    await onRequestNext()
  }

  // Format time helper
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }

  // Show empty state if no number
  if (!number) {
    return (
      <EmptyNumberCard 
        errorMessage={errorMessage}
        errorReason={errorReason}
        region={region}
      />
    )
  }

  // Show if next button should be displayed (disabled if rate limited)
  const shouldShowNextButton = showNextButton && 
    remainingCalls > 0 && 
    (rateLimited || statusSelected || canRequestNext)
  
  const isNextButtonDisabled = rateLimited && remainingWaitTime > 0

  return (
    <Card className="bg-card rounded-lg border shadow-soft">
      <Card.Content className="space-y-4 p-6">

        {/* Number Display */}
        <NumberDisplay 
          number={number} 
          onMakeCall={makeCall} 
        />

        {/* Calling History */}
        <CallingHistory phoneNumber={number?.phone_number} />

        {/* Status Selection */}
        {showUpdateButton && (
          <StatusSelector
            key={`${trackingId}-${number.phone_number}`}
            onStatusUpdate={handleStatusUpdate}
            loading={loading}
            trackingId={trackingId}
            phoneNumber={number.phone_number}
            onStatusSelected={() => setStatusSelected(true)}
            disabled={!hasClickedCall}
          />
        )}

        {/* Rate Limit Display */}
        {rateLimited && remainingWaitTime > 0 && (
          <div className="bg-orange-50 rounded-lg p-4 text-center border border-orange-200">
            <Icon name="Clock" className="w-12 h-12 mx-auto mb-3 text-orange-500" />
            <h3 className="text-lg font-bold text-orange-700 mb-2">
              অপেক্ষা করুন
            </h3>
            <p className="text-sm text-orange-600 mb-3">
              পরবর্তী নম্বর পেতে আরও কিছুক্ষণ অপেক্ষা করুন
            </p>
            <div className="text-2xl font-bold text-orange-600 mb-2">
              {formatTime(remainingWaitTime)}
            </div>
            <p className="text-xs text-orange-700">
              এই সময় পরে আবার চেষ্টা করুন
            </p>
          </div>
        )}

        {/* Next Button */}
        {shouldShowNextButton && (
          <NextNumberButton
            onRequestNext={handleRequestNext}
            loading={loading || isNextButtonDisabled}
            remainingCalls={remainingCalls}
            cooldownMessage={cooldownMessage}
            disabled={isNextButtonDisabled}
          />
        )}
      </Card.Content>
    </Card>
  )
}

export default WakeUpNumberCard